/* eslint-disable sort-keys */
/* eslint-disable max-lines */
import {
    AudienceMetadataContent,
    AudienceMetadataType,
    AudienceMetadataViewProps,
} from './types'
import {
    ClipboardListIcon,
    DocumentTextIcon,
    FingerPrintIcon,
    HeroArrowDownTrayIcon,
    HeroPencilSquareIcon,
    InformationCircleIcon,
} from '../Icon'

import {
    NinchatFile,
    QuestionnaireAnswers, Sso,
} from 'api/types/properties'

import React, {useContext} from 'react'

import {
    actions,
    attachmentButton,
    attachmentFileActions,
    attachmentFileInfo,
    attachmentFileName,
    attachmentImageWrapper,
    attachmentsFileInfo,
    attachmentsLabel,
    attachments as attachmentsStyle,
    audienceMetadataHeader,
    audienceMetadata as audienceMetadataItemWrapper,
    audienceMetadataWrapper,
    fingerprintWrapper,
    ssoMetadata,
} from './styles/AudienceMetadata'

import {AudienceMetadataHeaderView} from './components/AudienceMetadataHeaderView'
import BoostHistory from '../BoostHistory'
import Button from '../Button'
import {DelegateMetadataView} from './components/DelegateMetadataView'
import {EmbedContext} from 'components/EmbedContext'
import HtaConnector from '../HtaConnector'
import {Icon} from 'components/Icon/types'
import {MetadataView} from './components/MetadataView'

import PrescriptionRenewalSurveyResults from '../PrescriptionRenewalSurveyResults'
import Tags from 'components/Tags'
import User from 'components/User'

import {downloadFile} from 'utils/download'
import {fileUtil} from 'utils/file'
import {filterAnswers} from './utils/filterAnswers'
import {styled} from '../../stitches/config'
import useLightbox from 'hooks/lightbox'
import {useTranslation} from 'react-i18next'
import {useUsers} from 'hooks/ninchat'

const StyledAudienceMetadataWrapper = styled('div', audienceMetadataWrapper)
const StyledAudienceMetadataHeader = styled('div', audienceMetadataHeader)
const StyledAudienceMetadataItem = styled('div', audienceMetadataItemWrapper)
const StyledActions = styled('div', actions)
export const StyledSsoMetadata = styled('div', ssoMetadata)
const StyledFingerprintWrapper = styled('div', fingerprintWrapper)

const StyledAttachmentLink = styled('button', attachmentButton)
const StyledAttachments = styled('div', attachmentsStyle)
const StyledAttachmentsLabel = styled('div', attachmentsLabel)
const StyledAttachmentFilesInfo = styled('div', attachmentsFileInfo)
const StyledAttachmentFileInfo = styled('a', {
    ...attachmentFileInfo,
    '&&:hover, &&:focus-within': {[`& ${StyledAttachmentLink}`]: {display: 'flex'}},
})
const StyledAttachmentImageWrapper = styled('div', attachmentImageWrapper)
const StyledAttachmentFileName = styled('div', attachmentFileName)
const StyledAttachmentFileActions = styled('div', attachmentFileActions)

const GreenFingerprintIcon = () => <StyledFingerprintWrapper><FingerPrintIcon /></StyledFingerprintWrapper>

// eslint-disable-next-line complexity
const AudienceMetadataView = (props: AudienceMetadataViewProps): React.ReactElement => {
    const {
        attachments,
        boostHistory,
        compact,
        preAnswers,
        postAnswers,
        htaConnector,
        htiaConnector,
        exclude,
        keepHeader,
        lite,
        ninchat,
        secure,
        audienceMetadata,
        device,
        vars,
        openModal,
        prescriptionRenewalSurveyResults,
        realmId,
        user_id,
        sso,
        ssoDelegate,
        notes,
        tagIds,
    } = props

    const {t} = useTranslation()
    // eslint-disable-next-line no-unused-expressions
    useUsers([user_id || ''])[user_id || ''] || {}

    const {open} = useLightbox()

    const embedConfig = useContext(EmbedContext)
    const isWebView = embedConfig?.isWebView || false

    const defaultState = 0

    const [excludedCount, setExcludedCount] = React.useState(defaultState)

    // Count how many 1st level metadata keys are excluded
    const getExcludedCount = () => {
        // Remove possible duplicates from array
        const uniq = Array.from(new Set(exclude))
        let count = 0

        uniq.forEach((type) => {
            switch (type) {
                case 'ninchat':
                    count += Object.keys(ninchat || {}).length
                    break
                case 'secure':
                    count += Object.keys(secure || {}).length
                    break
                case 'other':
                    count += Object.keys(audienceMetadata || {}).length
                    break
                case 'device':
                    count += device ? 1 : 0
                    break
                case 'vars':
                    count += vars ? 1 : 0
                    break
                default:
            }
        })

        return count
    }

    React.useEffect(() => {
        setExcludedCount(getExcludedCount())
    }, [exclude])

    const audienceMetadataRef = React.useRef<HTMLDivElement>(null)
    const [responsive, setResponsive] = React.useState(false)

    React.useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            const rect = entries[0].contentRect
            setResponsive(rect.width < 480)
        })

        if (audienceMetadataRef && audienceMetadataRef.current) {
            observer.observe(audienceMetadataRef.current)
        }

        return () => {
            observer.unobserve(document.documentElement)
        }
    }, [compact])

    const handleOpenFile = (attachment: NinchatFile) => (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()

        if (attachment.file_attrs.type?.startsWith('image/')) {
            open(attachment?.file_url, {
                name: attachment.file_attrs.name,
                size: attachment.file_attrs.size,
                type: attachment.file_attrs.type,
            })
        } else {
            window.open(attachment?.file_url, '_blank', 'noopener noreferrer')
        }
    }

    const handleDownload = (attachment: NinchatFile) => (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        downloadFile(attachment.file_url, attachment.file_attrs.name)
    }

    const isExcluded = (type: AudienceMetadataType) => exclude?.indexOf(type) !== -1


    const tAuthenticate = t('Authenticated')
    const tAudienceMetadata = t('Audience metadata')
    const tPreaudienceQuestionnaireAnswer = t('Pre-audience questionnaire answers')
    const tPostaudienceQuestionnaireAnswer = t('Post-audience questionnaire answers')
    const tNotes = t('Notes')

    const hasAttachments = attachments && attachments.length > 0

    return (
        <StyledAudienceMetadataWrapper lite={lite}>
            {!lite || keepHeader
                ? (
                    <StyledAudienceMetadataHeader>
                        <User displayProtectedEmail={true} fallbackName={(sso?.['First name'] || secure?.claims?.preferred_username) as string} size='small' userId={user_id || ''} />
                    </StyledAudienceMetadataHeader>)
                : null}
            {sso && Object.entries(sso).length > 0
                ? (
                    <StyledAudienceMetadataItem lite={lite}>
                        <StyledSsoMetadata>
                            <AudienceMetadataHeaderView
                                Icon={GreenFingerprintIcon}
                                text={tAuthenticate}
                                values={{
                                    [tAuthenticate]: {
                                        ...sso,
                                        ...ssoDelegate && {Delegate: ssoDelegate},
                                    },
                                }}
                            />
                            <MetadataView compact={compact || responsive} level={0} metadata={sso} metadataType='sso' />
                        </StyledSsoMetadata>
                        <DelegateMetadataView
                            compact={compact || responsive}
                            delegates={ssoDelegate}
                            metadataType='ssoDelegate'
                        />
                    </StyledAudienceMetadataItem>
                )
                : null}
            {/* eslint-disable-next-line no-extra-parens, max-len */}
            {(Object.keys(ninchat || {}).length || Object.keys(secure || {}).length || Object.keys(audienceMetadata || {}).length || Object.keys(device || {}).length || Object.keys(vars || {}).length || excludedCount > 0) ?
                <StyledAudienceMetadataItem lite={lite} ref={audienceMetadataRef}>
                    <AudienceMetadataHeaderView
                        Icon={InformationCircleIcon as Icon}
                        text={tAudienceMetadata}
                        values={{
                            [tAudienceMetadata]: {
                                ...ninchat,
                                ...secure,
                                ...audienceMetadata,
                                // eslint-disable-next-line no-extra-parens
                                ...(device ? {device} : {}),
                                // eslint-disable-next-line no-extra-parens
                                ...(vars ? {vars} : {}),
                                ...sso,
                            },
                        }}
                    />
                    {ninchat && !isExcluded('ninchat') ? <MetadataView compact={compact || responsive} level={0} metadata={ninchat} metadataType='ninchat' /> : null}
                    {secure && !isExcluded('secure') ? <MetadataView compact={compact || responsive} level={0} metadata={secure as AudienceMetadataContent} metadataType='secure' /> : null}
                    {audienceMetadata && !isExcluded('other') ? <MetadataView compact={compact || responsive} level={0} metadata={audienceMetadata} metadataType='other' /> : null}
                    {device && !isExcluded('device') ? <MetadataView compact={compact || responsive} level={0} metadata={{device}} metadataType='device' /> : null}
                    {vars && !isExcluded('vars') ? <MetadataView compact={compact || responsive} level={0} metadata={{vars}} metadataType='vars' /> : null}
                    {excludedCount > 0 && openModal ?
                        <StyledActions>
                            <Button onPress={() => openModal(user_id, sso as Sso, secure)} size='small' text={t('Show {{count}} hidden items', {count: excludedCount})} />
                        </StyledActions>
                        : null}
                </StyledAudienceMetadataItem>
                : null}
            {/* eslint-disable-next-line no-extra-parens */}
            {((!!preAnswers && Object.keys(filterAnswers(preAnswers as QuestionnaireAnswers, t)).length > 0) || hasAttachments) ?
                <StyledAudienceMetadataItem lite={lite}>
                    <AudienceMetadataHeaderView
                        Icon={ClipboardListIcon}
                        text={tPreaudienceQuestionnaireAnswer}
                        values={{[tPreaudienceQuestionnaireAnswer]: {...filterAnswers(preAnswers as QuestionnaireAnswers, t)}}}
                    />
                    <MetadataView compact={compact || responsive} level={0} metadata={filterAnswers(preAnswers as QuestionnaireAnswers, t)} metadataType='preAnswers' />
                    {hasAttachments ?
                        <StyledAttachments>
                            <StyledAttachmentsLabel>{t('Files')}</StyledAttachmentsLabel>
                            <StyledAttachmentFilesInfo>
                                {attachments.map((attachment) => (
                                    <StyledAttachmentFileInfo href='' key={attachment.file_id} onClick={handleOpenFile(attachment)}>
                                        <StyledAttachmentImageWrapper hasThumbnail={!!attachment.thumbnail_url}>
                                            {attachment.thumbnail_url ? <img alt={attachment.file_attrs.name} src={attachment.thumbnail_url} /> : <DocumentTextIcon />}
                                        </StyledAttachmentImageWrapper>
                                        <StyledAttachmentFileName>
                                            <div>{attachment.file_attrs.name}</div>
                                            <div>{fileUtil.formatBytes(attachment.file_attrs.size || 0)}</div>
                                        </StyledAttachmentFileName>
                                        <StyledAttachmentFileActions>
                                            {!isWebView &&
                                                <StyledAttachmentLink onClick={handleDownload(attachment)} type='button'>
                                                    <HeroArrowDownTrayIcon />
                                                </StyledAttachmentLink>}
                                        </StyledAttachmentFileActions>
                                    </StyledAttachmentFileInfo>
                                ))}
                            </StyledAttachmentFilesInfo>
                        </StyledAttachments>
                        : null}
                </StyledAudienceMetadataItem>
                : null}
            {!!postAnswers && Object.keys(filterAnswers(postAnswers as QuestionnaireAnswers, t)).length > 0 && !isExcluded('postAnswers') && (
                <StyledAudienceMetadataItem lite={lite}>
                    <AudienceMetadataHeaderView
                        Icon={ClipboardListIcon}
                        text={tPostaudienceQuestionnaireAnswer}
                        values={{[tPostaudienceQuestionnaireAnswer]: {...filterAnswers(postAnswers as QuestionnaireAnswers, t)}}}
                    />
                    <MetadataView compact={compact || responsive} level={0} metadata={filterAnswers(postAnswers as QuestionnaireAnswers, t)} metadataType='postAnswers' />
                </StyledAudienceMetadataItem>
            )}
            {!!htaConnector && Object.entries(htaConnector).length > 0 &&
                <HtaConnector hta={htaConnector} />}
            {!!htiaConnector && Object.entries(htiaConnector).length > 0 &&
                <HtaConnector hta={htiaConnector} />}
            {!!boostHistory && Object.entries(boostHistory).length > 0 &&
                <BoostHistory history={boostHistory} />}
            {!!prescriptionRenewalSurveyResults && Object.entries(prescriptionRenewalSurveyResults).length > 0 &&
                <PrescriptionRenewalSurveyResults data={prescriptionRenewalSurveyResults} />}
            {!!realmId && !!tagIds && tagIds.length > 0 &&
                <Tags realmId={realmId} tagIds={tagIds} />}
            {!!notes && Object.keys(notes).length > 0 &&
                <StyledAudienceMetadataItem lite={lite}>
                    <AudienceMetadataHeaderView
                        Icon={HeroPencilSquareIcon as unknown as Icon}
                        text={tNotes}
                        values={{[tNotes]: notes}}
                    />
                    <MetadataView compact={compact || responsive} level={0} metadata={notes} metadataType='notes' />
                </StyledAudienceMetadataItem>}
        </StyledAudienceMetadataWrapper>
    )
}

export default AudienceMetadataView
